import { supabase } from "@/integrations/supabase/client";
import { printOrderByLocations } from "./order-printing";
import { printDeliveryReceipt } from "./delivery-printing";
import { logger } from "./logger";

/**
 * Trigger production printing for a public order (from QR code or Tablet)
 * This is called when auto-approve is enabled or when an order is manually approved
 */
export async function triggerProductionPrint(
  orderId: string, 
  restaurantId: string
): Promise<void> {
  try {
    // Fetch order with only UNPRINTED items (printed_at IS NULL)
    const { data: order, error } = await supabase
      .from("orders")
      .select(`
        id, 
        order_number, 
        table_number, 
        tab_number, 
        customer_name,
        customer_phone,
        delivery_address,
        notes,
        order_items(id, product_id, combo_id, product_name, quantity, notes, combo_selections, selected_addons, printed_at, source)
      `)
      .eq("id", orderId)
      .single();

    if (error || !order) {
      logger.error("Error fetching order for printing:", error);
      return;
    }

    // Filter to only unprinted items
    const allItems = order.order_items || [];
    const items = allItems.filter((item: any) => !item.printed_at && item.source !== 'waiter');
    
    if (items.length === 0) {
      logger.warn("No new items to print for order:", orderId);
      return;
    }

    // Build print items including combo details and addons
    const printItems = items.map((item: any) => {
      let itemNotes = item.notes || "";
      
      // Process selected addons (upsell)
      if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0) {
        const addonsText = item.selected_addons
          .map((a: any) => `+ ${a.name}`)
          .join(" | ");
        itemNotes = itemNotes 
          ? `${addonsText} | ${itemNotes}`
          : addonsText;
      }
      
      // If this is a combo item, add selection details to notes
      if (item.combo_id && item.combo_selections) {
        const selections = item.combo_selections;
        const selectionDetails: string[] = [];
        
        // Handle pizza flavors
        if (selections.selectedFlavors && selections.selectedFlavors.length > 0) {
          const flavors = selections.selectedFlavors.map((f: any) => f.productName).join(", ");
          selectionDetails.push(`Sabores: ${flavors}`);
        }
        
        // Handle combo choices (slots) - legacy format
        if (selections.selectedChoices && selections.selectedChoices.length > 0) {
          for (const choice of selections.selectedChoices) {
            selectionDetails.push(`${choice.choiceLabel}: ${choice.productName}`);
          }
        }
        
        // Handle slot selections - suporte a array (legado) e objeto (tablet)
        if (selections.slotSelections) {
          if (Array.isArray(selections.slotSelections)) {
            for (const slot of selections.slotSelections) {
              if (slot.selectedProducts) {
                const products = slot.selectedProducts.map((p: any) => p.productName).join(", ");
                selectionDetails.push(`${slot.slotLabel || 'Itens'}: ${products}`);
              }
            }
          } else {
            const allProducts = Object.values(selections.slotSelections)
              .flat()
              .map((p: any) => p.productName)
              .join(", ");
            if (allProducts) {
              selectionDetails.push(`Itens: ${allProducts}`);
            }
          }
        }
        
        if (selectionDetails.length > 0) {
          itemNotes = itemNotes 
            ? `${selectionDetails.join(" | ")} | ${itemNotes}`
            : selectionDetails.join(" | ");
        }
      }
      
      return {
        product_id: item.product_id || item.combo_id || "",
        product_name: item.product_name,
        quantity: item.quantity,
        notes: itemNotes || undefined,
      };
    });

    // Determine if delivery order
    const isDelivery = !!order.delivery_address;

    await printOrderByLocations(
      order.order_number,
      printItems,
      {
        type: isDelivery ? "delivery" : order.table_number ? "table" : "tab",
        tableNumber: order.table_number || undefined,
        tabNumber: order.tab_number || undefined,
        customerName: order.customer_name || undefined,
        waiterName: undefined,
        notes: order.notes || undefined,
        isDelivery,
        deliveryAddress: order.delivery_address || undefined,
        customerPhone: order.customer_phone || undefined,
      },
      restaurantId
    );

    // For delivery orders, also print the delivery driver receipt
    if (isDelivery) {
      await printDeliveryReceipt(orderId, restaurantId);
    }

    // Mark items as printed
    const itemIds = items.map((item: any) => item.id);
    if (itemIds.length > 0) {
      const { error: updateError } = await supabase
        .from("order_items")
        .update({ printed_at: new Date().toISOString() })
        .in("id", itemIds);
      
      if (updateError) {
        logger.error("Error marking items as printed:", updateError);
      } else {
        logger.log(`Marked ${itemIds.length} items as printed`);
      }
    }

    logger.log(`Production print triggered for order #${order.order_number}`);
  } catch (err) {
    logger.error("Error triggering production print:", err);
  }
}
